import React, { useState, useEffect } from "react";
import moment from "moment";
import Timeline, { TimelineHeaders, DateHeader, SidebarHeader } from "react-calendar-timeline";
import { FormattedMessage, useIntl } from "react-intl";

import { GanttBlockout, GanttWorkOrder } from "./ganttItem";
import { DRAG_SNAP_INCREMENT } from "./../taskDnD/dndConstants";

import SmallDropdownIcon from "../common/icons/arrows/smallDropdownIcon";

import "react-calendar-timeline/lib/Timeline.css";
import "./gantt.css";

var keys = {
    groupIdKey: "id", // technician.id
    groupTitleKey: "pein", // technician.pein

    itemIdKey: "workOrderNumber", // assignment.workOrderNumber
    itemTitleKey: "workOrderNumber", // assignment.workOrderNumber
    itemDivTitleKey: "workOrderNumber", // assignment.workOrderNumber
    itemGroupKey: "resourceAssignedTo", // assignment.resourceAssignedTo
    itemTimeStartKey: "start", // assignment.taskAssignedDate
    itemTimeEndKey: "end", // assignment.taskAssignedDate + assignment.duration
};

const noneSelected = 0;
const notSelected = 1;
const selected = 2;

/*
    Control to display a Gantt used to show technicians and the work
    they have been assigned.  

    props:
        
        ganttDate: day to show in the Gantt
        showSidebar: bool, show or hide the technicians in the left hand sidebar
        assignments: array of assignments to display in the Gantt
        technicians: array of technicians to display in the Gantt
        assignTask: (assignment, overrideOverlap: bool) => void
            callback used to assign the task
*/
const findOverlappingitems = (items) => {
    // need to flag items that are for the same resource and
    // overlap
    items
        .sort((a, b) => a.start - b.start)
        .forEach((currentItem, i) => {
            // loop through remaining items and find overlaps
            var remainingItems = items.slice(i + 1);

            remainingItems.forEach((remainingItem) => {
                if (remainingItem.resourceAssignedTo === currentItem.resourceAssignedTo) {
                    if (currentItem.start <= remainingItem.end && remainingItem.start < currentItem.end) {
                        currentItem.overlap = 1;
                        remainingItem.overlap = 2;
                    }
                }
            });
        });
};

export const Gantt = (props) => {
    const intl = useIntl();
    const showSidebar = props.showSidebar === undefined ? true : props.showSidebar;
    const { ganttDate, assignments, technicians, timelineRef, scrollRef, noTimeline, selectedLabourer } = props;

    // FMSGAS-4287: Need to make sure that the Gantt renders at least once with an
    // array of technicians.  Otherwise it enters a bad state where the times are not
    // correctly alligned with the headers
    const [renderWithTechs, setRenderWithTechs] = useState("withTechs");

    // props timeRange.  Start and end of times shown on Gantt in seconds
    // if the timeline is hidden set start and end to gantt data so
    // no hrs are visible
    const defDate = Date.now();
    const start = noTimeline ? defDate : ganttDate + props.timeRange.start * 1000;
    const end = noTimeline ? defDate : ganttDate + props.timeRange.end * 1000;

    useEffect(() => {
        moment.locale(intl.locale);
    }, [intl.locale]);

    // items from props.assignments
    const contractNumbers = props.filter ? props.filter.items.map((item) => item.contractNumber) : [];
    const tasks = props.filter
        ? props.tasks
              ?.filter((task) => props.filter.all || contractNumbers.includes(task?.contractNumber))
              .filter((item) => !props.zones || props.zones.all || props.zones.items.includes(item.zone))
        : [];
    const workOrderNumbers = tasks ? tasks.map((item) => item.workOrderNumber) : [];

    const items = assignments
        .filter((assignment) => workOrderNumbers.includes(assignment.workOrderNumber))
        // only show items that span the date range we are rendering
        .filter(
            (assignment) =>
                //assignment.taskAssignedDate + 0 <= end &&
                assignment.scheduledDate + 0 <= end &&
                //start <= assignment.taskAssignedDate + assignment.duration * 1000)
                start <= assignment.scheduledDate + assignment.duration * 1000,
        )
        // map assignments onto props required for the gantt
        .map((assignment) => ({
            ...assignment,
            type: "work-order",
            // start: assignment.taskAssignedDate + 0,
            // end: assignment.taskAssignedDate + assignment.duration * 1000,
            start: assignment.scheduledDate + 0,
            end: assignment.scheduledDate + assignment.duration * 1000,
            overlap: 0,
        }));

    findOverlappingitems(items);

    // groups from props.technicians
    const groups = technicians.map((tech) => ({
        ...tech,
        selected: selectedLabourer ? (tech.id === selectedLabourer.id ? selected : notSelected) : noneSelected,
    }));

    const handleLabClick = (e, technician) => {
        e.stopPropagation();
        props.setSelectedLabourer(technician);
    };

    // Group is rendered as
    // firstName lastName
    //               pein
    const groupRenderer = ({ group }) => {
        return (
            <div className={"cmis-group " + (group.selected === notSelected ? "not-selected" : "")} onClick={(e) => handleLabClick(e, group)}>
                <div className="cmis-group-title">
                    <div className="cmis-group-name">{group.firstName + " " + group.lastName}</div>
                    <div className="cmis-group-id">{group.pein}</div>
                </div>
            </div>
        );
    };

    // Item is rendered differently if the type is a blockout
    const itemRenderer = ({ item, itemContext, getItemProps, getResizeProps }) => {
        var itemProps = getItemProps({ className: item.type });
        if (item.overlap > 0) {
            var halfHeight = itemContext.dimensions.height / 2;
            itemProps.style.height = `${halfHeight - 2.5}px`;

            if (item.overlap === 2) {
                itemProps.style.top = `${itemContext.dimensions.top + halfHeight + 2.5}px`;
            }
        }

        return (
            <div {...itemProps} onMouseDown={() => {}}>
                {item.type === "blockout" ? (
                    <GanttBlockout
                        item={item}
                        itemContext={itemContext}
                        notSelected={selectedLabourer && selectedLabourer.id !== item.resourceAssignedTo}
                    />
                ) : (
                    <GanttWorkOrder
                        item={item}
                        itemContext={itemContext}
                        notSelected={selectedLabourer && selectedLabourer.id !== item.resourceAssignedTo}
                    />
                )}
            </div>
        );
    };

    // Add even and odd classes to vertical columns' class
    // we can then alternate the colors using different styles for odd and
    // even
    const verticalLineClassNamesForTime = (timeStart, timeEnd) => {
        const currentTimeStart = moment(timeStart);
        const startHour = currentTimeStart.hour();

        if (startHour < 5 || startHour > 21) {
            return ["cmis-vt-even"];
        } else if (startHour % 2 === 0) {
            return ["cmis-vt-even"];
        } else {
            return ["cmis-vt-odd"];
        }
    };

    const horizontalLineClassNamesForGroup = (group) => {
        if (group.selected === notSelected) return ["not-selected"];
    };

    // don't render if the techs array is null.  If the previous render
    // was empty set a new key to reload the Gantt component.  Need to do this
    // as if we render the gantt with no rows the next renders with rows will have
    // their columns in correctly placed.
    if (technicians) {
        if (technicians.length === 0 && renderWithTechs !== "noTechs") {
            setRenderWithTechs("noTechs");
        } else if (technicians.length !== 0 && renderWithTechs !== "withTechs") {
            setRenderWithTechs("withTechs");
        }
    } else {
        return null;
    }

    return (
        <div
            style={{
                width: props.width + "px",
            }}>
            <Timeline
                ref={timelineRef}
                scrollRef={scrollRef}
                className="cmis-gantt initial-overflow"
                key={renderWithTechs + intl.locale}
                groups={groups}
                items={items}
                keys={keys}
                visibleTimeStart={start}
                visibleTimeEnd={end}
                stackItems={false}
                canResize={false}
                canMove={true}
                dragSnap={DRAG_SNAP_INCREMENT}
                lineHeight={70}
                itemHeightRatio={0.77}
                sidebarWidth={showSidebar ? 160 : 0}
                groupRenderer={groupRenderer}
                itemRenderer={itemRenderer}
                verticalLineClassNamesForTime={verticalLineClassNamesForTime}
                horizontalLineClassNamesForGroup={horizontalLineClassNamesForGroup}>
                <TimelineHeaders className="cmis-gantt-header sticky">
                    {showSidebar && (
                        <SidebarHeader className="cmis-sidebar-header">
                            {({ getRootProps }) => (
                                <div className="cmis-sidebar-header-content" {...getRootProps()}>
                                    <span>
                                        <FormattedMessage
                                            id="gantt.all-labourers"
                                            defaultMessage="All labourers"
                                            description="title to column of labourers"
                                        />
                                        <SmallDropdownIcon />
                                    </span>
                                </div>
                            )}
                        </SidebarHeader>
                    )}
                    <DateHeader className="cmis-day-header" unit="day" labelFormat="dddd, MMM DD YYYY" />
                    <DateHeader
                        className="cmis-hour-header"
                        style={{
                            height: "16px",
                        }}
                        unit="hour"
                        labelFormat="H:mm"
                        intervalRenderer={({ getIntervalProps, intervalContext, data }) => (
                            <div {...getIntervalProps()} className={"rct-dateHeader "}>
                                <div className="cmis-hr-caption">{intervalContext.intervalText}</div>
                                <div className="cmis-half-hr-marker" />
                            </div>
                        )}
                    />
                </TimelineHeaders>
            </Timeline>
        </div>
    );
};

export default Gantt;
